import React from 'react';

import { getPasswordPolicyViolationMessage } from '@/_helpers/passwordPolicy';



/**

 * Character classes: lowercase, uppercase, digit, special (non-alphanumeric).

 * Aligned with common UX: length matters (NIST min 8); variety estimates entropy

 * (OWASP-style guidance: mix of character types as a strength signal).

 */

function getCharacterVariety(password) {

    if (!password) return 0;

    return [

        /[a-z]/.test(password),

        /[A-Z]/.test(password),

        /[0-9]/.test(password),

        /[^A-Za-z0-9]/.test(password),

    ].filter(Boolean).length;

}



function getLevel(password) {

    const len = password.length;

    if (/\s/.test(password)) {

        return { label: 'Weak', filled: 1, color: 'bg-red-500' };

    }

    const v = getCharacterVariety(password);



    if (len < 8) {

        return { label: 'Weak', filled: 1, color: 'bg-red-500' };

    }



    // Strong: all four types at 8+ chars, or long passwords with good variety

    if (

        v === 4 ||

        (len >= 16 && v >= 3) ||

        (len >= 20 && v >= 2)

    ) {

        return { label: 'Strong', filled: 4, color: 'bg-green-500' };

    }



    // Good: three types, or 12+ with two types, or long single-class passphrases

    if (v === 3 || (len >= 12 && v === 2) || (len >= 18 && v === 1)) {

        return { label: 'Good', filled: 3, color: 'bg-yellow-500' };

    }



    if (v === 2) {

        return { label: 'Fair', filled: 2, color: 'bg-amber-400' };

    }



    return { label: 'Weak', filled: 1, color: 'bg-red-500' };

}



/** True if password contains any whitespace (not allowed). */

export function passwordHasDisallowedWhitespace(password) {

    return /\s/.test(String(password || ''));

}



/**

 * Entropy-style strength + policy (whitespace, personal info, digit patterns).

 * @param {string} password

 * @param {{ email?: string, username?: string, fullName?: string } | null | undefined} policyContext

 */

export function isPasswordStrong(password, policyContext) {

    const p = String(password || '');

    if (passwordHasDisallowedWhitespace(p)) return false;

    if (getPasswordPolicyViolationMessage(p, policyContext)) return false;

    return getLevel(p).label === 'Strong';

}



export const PASSWORD_NOT_STRONG_ENOUGH =

    'This password is not strong enough. Raise the meter to Strong: mix uppercase, lowercase, numbers, and symbols and use a longer passphrase.';



export const PASSWORD_SPACE_NOT_ALLOWED = 'Spaces are not allowed in your password.';



const HINT =

    'At least 8 characters, no spaces. Do not use your name, username, or email, or obvious number sequences (e.g. 123 or 1234). Mix upper, lower, numbers, and symbols.';



/**

 * @param {object} props

 * @param {string} props.password

 * @param {{ email?: string, username?: string, fullName?: string } | null} [props.policyContext]

 */

export default function PasswordStrengthMeter({ password, policyContext }) {

    const pwd = password || '';

    const hasSpace = pwd.length > 0 && passwordHasDisallowedWhitespace(pwd);

    const policyMsg =

        pwd.length > 0 && !hasSpace ? getPasswordPolicyViolationMessage(pwd, policyContext) : null;

    const baseLevel = getLevel(pwd);

    const policyBlocked = Boolean(policyMsg);

    const filled = hasSpace || policyBlocked ? 1 : baseLevel.filled;

    const color = hasSpace || policyBlocked ? 'bg-red-500' : baseLevel.color;

    const label = baseLevel.label;



    return (

        <div className="space-y-1 pt-0.5">

            {password ? (

                <>

                    <div className="flex gap-1">

                        {[1, 2, 3, 4].map((i) => (

                            <div

                                key={i}

                                className={`h-1 flex-1 rounded-full transition-all duration-300 ${i <= filled ? color : 'bg-muted'}`}

                            />

                        ))}

                    </div>

                    <p

                        className={`text-xs transition-colors duration-300 font-medium ${

                            hasSpace || policyBlocked

                                ? 'text-left text-destructive'

                                : filled === 1

                                  ? 'text-right text-red-500'

                                  : filled === 2

                                    ? 'text-right text-amber-500'

                                    : filled === 3

                                      ? 'text-right text-yellow-500'

                                      : 'text-right text-green-600'

                        }`}

                    >

                        {hasSpace ? PASSWORD_SPACE_NOT_ALLOWED : policyMsg || label}

                    </p>

                </>

            ) : null}

            <p className="text-xs text-muted-foreground">{HINT}</p>

        </div>

    );

}



export { getPasswordPolicyViolationMessage } from '@/_helpers/passwordPolicy';


